import type { ApolloClient, NormalizedCacheObject } from '@apollo/client'
import { gql } from '@apollo/client'

import { PAGE_EDITOR_REVISIONS_PAGE_SIZE } from 'js/page/PageEditor/usePageEditorRevisions'

import { GET_PAGE_EDITOR_REVISIONS } from '../components/PageEditorRevisions/PageEditorRevisions.queries'

const GET_LATEST_PAGE_EDITOR_REVISION = gql`
    query LatestPageEditorRevision($guid: String!) {
        revisions(containerGuid: $guid, offset: 0, limit: 1) {
            edges {
                guid
                description
                timeCreated
                authors {
                    guid
                    name
                }
                type
                statusPublishedChanged
            }
        }
    }
`

type RevisionListEdge = {
    guid: string
    description?: string | null
    timeCreated: string
    authors?: Array<{ guid?: string; name?: string | null } | null> | null
    type: string
    statusPublishedChanged?: string | null
}

type PageEditorRevisionsQueryData = {
    revisions?: {
        total?: number
        edges?: Array<RevisionListEdge | null> | null
    } | null
}

export const prependLatestPageEditorRevisionToCache = async (
    client: ApolloClient<NormalizedCacheObject>,
    containerGuid: string,
) => {
    const { data: freshData } =
        await client.query<PageEditorRevisionsQueryData>({
            query: GET_LATEST_PAGE_EDITOR_REVISION,
            variables: { guid: containerGuid },
            fetchPolicy: 'no-cache',
        })

    const newRevision = freshData?.revisions?.edges?.[0] as
        | RevisionListEdge
        | undefined

    if (!newRevision) {
        return
    }

    // Merge into the cached list query so fetch-more edges are preserved.
    client.cache.updateQuery(
        {
            query: GET_PAGE_EDITOR_REVISIONS,
            variables: {
                guid: containerGuid,
                offset: 0,
                limit: PAGE_EDITOR_REVISIONS_PAGE_SIZE,
            },
        },
        (existing: PageEditorRevisionsQueryData | null) => {
            if (!existing?.revisions) {
                return existing
            }

            const edges = (existing.revisions.edges?.filter(Boolean) ??
                []) as RevisionListEdge[]

            if (edges[0]?.guid === newRevision.guid) {
                return {
                    ...existing,
                    revisions: {
                        ...existing.revisions,
                        edges: [newRevision, ...edges.slice(1)],
                    },
                }
            }

            return {
                ...existing,
                revisions: {
                    ...existing.revisions,
                    total: (existing.revisions.total ?? 0) + 1,
                    edges: [newRevision, ...edges],
                },
            }
        },
    )
}
